import { useEffect } from "react";
import { useUserCache } from "../context/UserCacheContext";
import useGetProfile from "../features/user/hooks/useGetProfile";
import { UserModel } from "../features/user/models/userModel";
import { useAuth } from "../features/auth/hooks/useAuth";

const CACHE_DURATION = 60000; // Cache duration in milliseconds (e.g., 60 seconds)

const useCachedProfile = (): {
  user: UserModel | null;
  loading: boolean;
  error: any;
  forceRefresh: () => Promise<void>;
} => {
  const { user, setUser, lastFetched, setLastFetched } = useUserCache();
  const { loading, error, refetch } = useGetProfile();
  const { isAuthenticated } = useAuth();

  const forceRefresh = async () => {
    if (isAuthenticated) {
      try {
        const result = await refetch();
        const fetchedUser = result.data?.me;
        if (fetchedUser) {
          setUser(fetchedUser);
          setLastFetched(Date.now());
        }
      } catch (err) {
        console.error('Error force refreshing profile:', err);
      }
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      const now = Date.now();
      if (!user || now - lastFetched > CACHE_DURATION) {
        const fetchData = async () => {
          const result = await refetch();
          const fetchedUser = result.data?.me;
          if (fetchedUser) {
            setUser(fetchedUser);
            setLastFetched(now);
          }
        };
        fetchData();
      }
    } else {
      // Clear user cache when not authenticated to prevent stale data
      if (user !== null) {
        setUser(null);
        setLastFetched(0);
      }
    }
  }, [user, lastFetched, setUser, setLastFetched, refetch, isAuthenticated]);

  return { user, loading, error, forceRefresh };
};

export default useCachedProfile;
